import axios from '@/lib/axios'
import { buildAdminRequestConfig, type ScopedAdminQueryKey } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AddCartItemInput,
  AddCustomCartItemInput,
  ApplyCartDiscountInput,
  ApplyCartTipInput,
  CalculateCartTaxAmountInput,
  Cart,
  CartDiscountSuggestions,
  CartItemMutationResponse,
  CartPartialRefundResponse,
  CartPaymentActionResponse,
  CartPaymentResponse,
  CheckedCartPaymentResponse,
  CashPaymentInput,
  CheckPaymentInput,
  CreateCartInput,
  CreatedCart,
  DeletedCart,
  FinalizeCartSalesOrderInput,
  OrganizationScope,
  PosCart,
  PosCartSaveRequest,
  PosCartUnsaveRequest,
  PrepareCardPaymentInput,
  RecoverablePosCartSummary,
  RefundCartPartialPaymentsInput,
  RemoveCartItemInput,
  SavedPosCartList,
  UpdateCartInput,
  UpdateCartItemInput,
} from './carts.types'

// Query-key entries are untyped (ScopedAdminQueryRoot), so ids interpolate as the key holds them.
type CartQueryContext = Pick<QueryFunctionContext<ScopedAdminQueryKey>, 'queryKey'> & {
  signal?: AbortSignal
}

interface CartScope extends OrganizationScope {
  cartId: number
}

export async function createCart({ organizationId, ...data }: OrganizationScope & CreateCartInput) {
  return axios.post<CreatedCart>('/cart', data, buildAdminRequestConfig({ organizationId }))
}

export async function getCart({ queryKey: [{ organizationId, cartId }] }: CartQueryContext) {
  return axios.get<Cart>(`/cart/${String(cartId)}`, buildAdminRequestConfig({ organizationId }))
}

export async function getPosCart({ queryKey: [{ organizationId, cartId }] }: CartQueryContext) {
  return axios.get<PosCart>(
    `/pos/carts/${String(cartId)}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function addCartItem({
  organizationId,
  cartId,
  variantId,
  quantity,
  subscriptionFrequency,
}: AddCartItemInput) {
  return axios.post<CartItemMutationResponse>(
    `/cart/${cartId}/cartitems`,
    {
      variant_id: variantId,
      quantity,
      ...(subscriptionFrequency ? { subscription_frequency: subscriptionFrequency } : {}),
    },
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

export async function getRecoverablePosCartSummary({
  queryKey: [{ organizationId, excludeCartId }],
}: CartQueryContext) {
  return axios.get<RecoverablePosCartSummary>(
    '/cart/recoverable-pos/summary',
    buildAdminRequestConfig({
      organizationId,
      params: excludeCartId ? { exclude_cart_id: excludeCartId } : {},
    })
  )
}

export async function getSavedPosCarts({
  queryKey: [{ organizationId, filters = {}, page = 1, pageSize = 20 }],
  signal,
}: CartQueryContext) {
  return axios.get<SavedPosCartList>(
    '/pos/saved-carts',
    buildAdminRequestConfig({
      organizationId,
      // The key holds the SavedPosCartFilters object; its entry is untyped here.
      params: { ...(typeof filters === 'object' ? filters : {}), page, page_size: pageSize },
      ...(signal ? { signal } : {}),
    })
  )
}

export async function savePosCart({ organizationId, cartId, label }: PosCartSaveRequest) {
  return axios.post<PosCart>(
    `/pos/carts/${cartId}/save`,
    { label },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function unsavePosCart({ organizationId, cartId }: PosCartUnsaveRequest) {
  return axios.delete<PosCart>(
    `/pos/carts/${cartId}/save`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateCartItem({
  organizationId,
  cartId,
  cartItemId,
  quantity,
  customItem,
  itemDetails,
  variantId,
  subscriptionFrequency,
}: UpdateCartItemInput) {
  return axios.put<CartItemMutationResponse>(
    `/cart/${cartId}/cartitems/${cartItemId}`,
    {
      ...(quantity !== undefined ? { quantity } : {}),
      ...(customItem !== undefined ? { custom_item: customItem } : {}),
      ...(itemDetails !== undefined ? { item_details: itemDetails } : {}),
      ...(variantId ? { variant_id: variantId } : {}),
      ...(subscriptionFrequency ? { subscription_frequency: subscriptionFrequency } : {}),
    },
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

export async function addCustomCartItem({
  organizationId,
  cartId,
  quantity,
  customItem,
}: AddCustomCartItemInput) {
  return axios.post<CartItemMutationResponse>(
    `/cart/${cartId}/cartitems`,
    { quantity, custom_item: customItem },
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

export async function removeCartItem({ organizationId, cartId, cartItemId }: RemoveCartItemInput) {
  return axios.delete<CartItemMutationResponse>(
    `/cart/${cartId}/cartitems/${cartItemId}`,
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

/** Answers with the PosCartResource for POS carts, else the CartResource. */
export async function updateCart({ organizationId, cartId, data }: UpdateCartInput) {
  return axios.put<Cart>(
    `/cart/${cartId}`,
    data,
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

/** Answers like `updateCart`. */
export async function applyCartDiscount({ organizationId, cartId, data }: ApplyCartDiscountInput) {
  return axios.post<Cart>(
    `/cart/${cartId}/discounts`,
    data,
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

export async function getCartDiscountSuggestions({
  queryKey: [{ organizationId, cartId, search }],
  signal,
}: CartQueryContext) {
  // cartQueryKeys.discountSuggestions always keys the search as a string.
  const trimmedSearch = typeof search === 'string' ? search.trim() : ''

  return axios.get<CartDiscountSuggestions>(
    `/cart/${String(cartId)}/discount-suggestions`,
    buildAdminRequestConfig({
      organizationId,
      signal,
      params: {
        view: 'pos',
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      },
    })
  )
}

/** Answers like `updateCart`. */
export async function applyCartTip({ organizationId, cartId, data }: ApplyCartTipInput) {
  return axios.post<Cart>(
    `/cart/${cartId}/tip`,
    data,
    buildAdminRequestConfig({ organizationId, params: { view: 'pos' } })
  )
}

export async function deleteCart({ organizationId, cartId }: CartScope) {
  return axios.delete<DeletedCart>(`/cart/${cartId}`, buildAdminRequestConfig({ organizationId }))
}

export async function prepareCardPayment({
  organizationId,
  cartId,
  amount,
  customerTotal,
  paymentMethod,
  savePaymentMethod,
  savedPaymentMethodId,
  paymentEntrySessionId,
  subscriptionTermsAccepted,
  subscriptionTermsVersion,
  organizationWorkstationId,
  organizationWorkstationClaim,
}: PrepareCardPaymentInput) {
  const payload: Record<string, unknown> = {}

  if (paymentEntrySessionId) payload.payment_entry_session_id = paymentEntrySessionId

  if (amount !== undefined) {
    payload.amount = amount
  }

  if (customerTotal !== undefined) {
    payload.customer_total = customerTotal
  }

  if (paymentMethod !== undefined) {
    payload.payment_method = paymentMethod
  }

  if (savePaymentMethod !== undefined) {
    payload.save_payment_method = savePaymentMethod
  }

  if (savedPaymentMethodId) {
    payload.saved_payment_method_id = savedPaymentMethodId
  }

  if (subscriptionTermsAccepted !== undefined) {
    payload.subscription_terms_accepted = subscriptionTermsAccepted
  }

  if (subscriptionTermsVersion) {
    payload.subscription_terms_version = subscriptionTermsVersion
  }

  if (organizationWorkstationId !== undefined) {
    payload.organization_workstation_id = organizationWorkstationId
    payload.organization_workstation_claim = organizationWorkstationClaim ?? null
  }

  return axios.post<CartPaymentActionResponse>(
    `/cart/${cartId}/card-payment`,
    payload,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Cancels the named card payment while it's still the cart's open card attempt. */
export async function cancelPreparedCardPayment({
  organizationId,
  cartId,
  paymentId,
}: CartScope & { paymentId: number }) {
  return axios.delete<CartPaymentActionResponse>(
    `/cart/${cartId}/card-payment/${paymentId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Asks the server to settle a card payment from what Stripe reports now. */
export async function checkCartPayment({
  organizationId,
  cartId,
  paymentId,
}: CartScope & { paymentId: number }) {
  return axios.post<CheckedCartPaymentResponse>(
    `/cart/${cartId}/payments/${paymentId}/check`,
    {},
    buildAdminRequestConfig({ organizationId })
  )
}

// No API route serves the two terminal endpoints below, so their responses stay untyped.
export async function createTerminalPaymentIntent({
  organizationId,
  ...data
}: OrganizationScope & Record<string, unknown>) {
  return axios.post<unknown>(
    '/cart/payment-intent',
    data,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function processTerminalPayment({
  organizationId,
  readerId,
  paymentIntentId,
}: OrganizationScope & { readerId: string; paymentIntentId: string }) {
  return axios.post<unknown>(
    `/cart/readers/${readerId}/process-payment`,
    {
      payment_intent_id: paymentIntentId,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCashPayment({
  organizationId,
  cartId,
  amount,
  customerTotal,
  tenderedAmount,
  reference,
  cashDrawerSessionId,
  idempotencyKey,
  organizationWorkstationId,
  organizationWorkstationClaim,
}: CashPaymentInput) {
  return axios.post<CartPaymentActionResponse>(
    `/cart/${cartId}/cash-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      ...(tenderedAmount !== undefined ? { tendered_amount: tenderedAmount } : {}),
      ...(organizationWorkstationId !== undefined
        ? {
            organization_workstation_id: organizationWorkstationId,
            organization_workstation_claim: organizationWorkstationClaim ?? null,
          }
        : {}),
      ...(reference ? { reference } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      idempotency_key: idempotencyKey,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createCheckPayment({
  organizationId,
  cartId,
  amount,
  customerTotal,
  reference,
  cashDrawerSessionId,
  idempotencyKey,
  organizationWorkstationId,
  organizationWorkstationClaim,
}: CheckPaymentInput) {
  return axios.post<CartPaymentActionResponse>(
    `/cart/${cartId}/check-payment`,
    {
      ...(amount !== undefined ? { amount } : {}),
      ...(customerTotal !== undefined ? { customer_total: customerTotal } : {}),
      ...(organizationWorkstationId !== undefined
        ? {
            organization_workstation_id: organizationWorkstationId,
            organization_workstation_claim: organizationWorkstationClaim ?? null,
          }
        : {}),
      ...(reference ? { reference } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
      idempotency_key: idempotencyKey,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function finalizeCartSalesOrder({
  organizationId,
  cartId,
  paymentMode,
  allowCreditLimitOverride,
  organizationWorkstationId,
  organizationWorkstationClaim,
}: FinalizeCartSalesOrderInput) {
  return axios.post<CartPaymentResponse>(
    `/cart/${cartId}/sales-order`,
    {
      payment_mode: paymentMode,
      ...(organizationWorkstationId !== undefined
        ? {
            organization_workstation_id: organizationWorkstationId,
            organization_workstation_claim: organizationWorkstationClaim ?? null,
          }
        : {}),
      ...(allowCreditLimitOverride ? { allow_credit_limit_override: true } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refundCartPartialPayments({
  organizationId,
  cartId,
  idempotencyKey,
  note,
  cashDrawerSessionId,
}: RefundCartPartialPaymentsInput) {
  return axios.post<CartPartialRefundResponse>(
    `/cart/${cartId}/refund-partial-payments`,
    {
      idempotency_key: idempotencyKey,
      ...(note ? { note } : {}),
      ...(cashDrawerSessionId ? { cash_drawer_session_id: cashDrawerSessionId } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

// The POS only checks that the address is taxable; the tax result is not read.
export async function calculateCartTaxAmount({
  organizationId,
  cartId,
  data,
}: CalculateCartTaxAmountInput) {
  return axios.post<unknown>(
    `/cart/${cartId}/calculate-tax-amount`,
    data,
    buildAdminRequestConfig({ organizationId })
  )
}
